import { useParams, useNavigate } from 'react-router-dom';
import { Edit, Trash2, Plus } from 'lucide-react';
import { useContact, useContacts } from '@/composables/crm/useContacts';
import { useNotes } from '@/composables/crm/useNotes';
import { useActivities } from '@/composables/crm/useActivities';
import { useTasks } from '@/composables/crm/useTasks';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FormDialog } from '@/components/FormDialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatDate, formatFullName, formatCurrency } from '@/lib/formatters';
import { useState } from 'react';

const activityTypes = [
  { label: 'Call', value: 'CALL' },
  { label: 'Email', value: 'EMAIL' },
  { label: 'Meeting', value: 'MEETING' },
  { label: 'Note', value: 'NOTE' },
  { label: 'Task', value: 'TASK' },
];

const priorityOptions = [
  { label: 'Low', value: 'LOW' },
  { label: 'Medium', value: 'MEDIUM' },
  { label: 'High', value: 'HIGH' },
  { label: 'Urgent', value: 'URGENT' },
];

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { contact, isLoading, activities, notes, deals, tasks, invalidate } = useContact(id!);
  const { deleteContact } = useContacts();
  const { createNote } = useNotes();
  const { createActivity } = useActivities();
  const { createTask } = useTasks();

  const [showDelete, setShowDelete] = useState(false);
  const [noteText, setNoteText] = useState('');

  const [showActivity, setShowActivity] = useState(false);
  const [activityForm, setActivityForm] = useState({ type: 'CALL', subject: '', description: '' });

  const [showTask, setShowTask] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', priority: 'MEDIUM', dueDate: '' });

  if (isLoading) return <LoadingSpinner />;
  if (!contact) return <div className="p-8 text-center">Contact not found</div>;

  const contactName = formatFullName(contact.firstName, contact.lastName);

  return (
    <PageContainer backTo
      title={contactName}
      description={contact.email || contact.company?.name || ''}
      actions={
        <>
          <Button variant="outline" onClick={() => navigate(`/contacts/${contact.id}/edit`)}><Edit className="mr-2 h-4 w-4" />Edit</Button>
          <Button variant="destructive" onClick={() => setShowDelete(true)}><Trash2 className="mr-2 h-4 w-4" />Delete</Button>
        </>
      }
    >
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Contact Info</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span>{contact.email || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Phone</span><span>{contact.phone || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Mobile</span><span>{contact.mobile || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Job Title</span><span>{contact.jobTitle || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Department</span><span>{contact.department || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Company</span><span>{contact.company?.name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span>{formatDate(contact.createdAt)}</span></div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2">
          <Tabs defaultValue="notes">
            <TabsList>
              <TabsTrigger value="notes">Notes ({notes.length})</TabsTrigger>
              <TabsTrigger value="activities">Activities ({activities.length})</TabsTrigger>
              <TabsTrigger value="tasks">Tasks ({tasks.length})</TabsTrigger>
              <TabsTrigger value="deals">Deals ({deals.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="notes" className="space-y-4 mt-4">
              <Card>
                <CardContent className="p-4 space-y-2">
                  <Textarea placeholder="Add a note..." value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={3} />
                  <div className="flex justify-end">
                    <Button size="sm" disabled={!noteText.trim() || createNote.isPending} onClick={() => { createNote.mutate({ content: noteText.trim(), contactId: contact.id }, { onSuccess: () => setNoteText('') }); }}>Add Note</Button>
                  </div>
                </CardContent>
              </Card>
              {notes.map((n) => (
                <Card key={n.id}><CardContent className="p-4"><p className="text-sm">{n.content}</p><p className="text-xs text-muted-foreground mt-2">{formatDate(n.createdAt)}</p></CardContent></Card>
              ))}
              {notes.length === 0 && <p className="text-muted-foreground text-center py-8">No notes yet</p>}
            </TabsContent>

            <TabsContent value="activities" className="space-y-4 mt-4">
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => setShowActivity(true)}>
                  <Plus className="mr-1 h-4 w-4" /> Log Activity
                </Button>
              </div>
              {activities.map((a) => (
                <Card key={a.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium">{a.subject}</p>
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">{a.type.replace('_', ' ')}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">{formatDate(a.occurredAt)}</p>
                        {a.description && <p className="text-sm mt-1 text-muted-foreground">{a.description}</p>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {activities.length === 0 && <p className="text-muted-foreground text-center py-8">No activities yet</p>}
            </TabsContent>

            <TabsContent value="tasks" className="space-y-4 mt-4">
              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={() => setShowTask(true)}>
                  <Plus className="mr-1 h-4 w-4" /> Add Task
                </Button>
              </div>
              {tasks.map((t) => (
                <Card key={t.id}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div><p className="text-sm font-medium">{t.title}</p><p className="text-xs text-muted-foreground">{t.dueDate ? `Due: ${formatDate(t.dueDate)}` : 'No due date'}</p></div>
                    <StatusBadge status={t.status} type="task" />
                  </CardContent>
                </Card>
              ))}
              {tasks.length === 0 && <p className="text-muted-foreground text-center py-8">No tasks yet</p>}
            </TabsContent>

            <TabsContent value="deals" className="space-y-4 mt-4">
              {deals.map((row) => {
                // Endpoint returns DealContact[] with the Deal nested under `.deal`.
                // Fall back to the row itself so this stays compatible if the API ever returns flat Deals.
                const d = (row as { deal?: typeof row }).deal ?? row;
                return (
                  <Card key={(row as { id: string }).id}>
                    <CardContent className="p-4 flex justify-between items-center">
                      <div><p className="font-medium">{d.title}</p><p className="text-xs text-muted-foreground">{d.company?.name}</p></div>
                      <div className="text-right"><p className="font-semibold">{formatCurrency(d.value)}</p><StatusBadge status={d.status} type="deal" /></div>
                    </CardContent>
                  </Card>
                );
              })}
              {deals.length === 0 && <p className="text-muted-foreground text-center py-8">No deals yet</p>}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <ConfirmDialog open={showDelete} onOpenChange={setShowDelete} title="Delete Contact" description="Are you sure? This cannot be undone." variant="destructive" confirmLabel="Delete" onConfirm={() => { deleteContact.mutate({ id: contact.id }); navigate('/contacts'); }} isLoading={deleteContact.isPending} />

      {/* Add Activity Dialog */}
      <FormDialog
        open={showActivity}
        onOpenChange={(open) => { setShowActivity(open); if (!open) setActivityForm({ type: 'CALL', subject: '', description: '' }); }}
        title="Log Activity"
        description="Record an interaction with this contact"
        submitLabel="Log Activity"
        isSubmitting={createActivity.isPending}
        onSubmit={() => {
          if (!activityForm.subject.trim()) return;
          createActivity.mutate({ ...activityForm, contactId: contact.id } as Parameters<typeof createActivity.mutate>[0], {
            onSuccess: () => { invalidate(); setShowActivity(false); setActivityForm({ type: 'CALL', subject: '', description: '' }); },
          });
        }}
      >
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Type</Label>
            <Select value={activityForm.type} onValueChange={(v) => setActivityForm((p) => ({ ...p, type: v }))}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{activityTypes.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Subject *</Label>
            <Input className="mt-1" placeholder="e.g. Follow-up call" value={activityForm.subject} onChange={(e) => setActivityForm((p) => ({ ...p, subject: e.target.value }))} />
          </div>
          <div>
            <Label className="text-sm">Description</Label>
            <Textarea className="mt-1" placeholder="Details..." rows={3} value={activityForm.description} onChange={(e) => setActivityForm((p) => ({ ...p, description: e.target.value }))} />
          </div>
        </div>
      </FormDialog>

      {/* Add Task Dialog */}
      <FormDialog
        open={showTask}
        onOpenChange={(open) => { setShowTask(open); if (!open) setTaskForm({ title: '', description: '', priority: 'MEDIUM', dueDate: '' }); }}
        title="Add Task"
        description="Create a task for this contact"
        submitLabel="Create Task"
        isSubmitting={createTask.isPending}
        onSubmit={() => {
          if (!taskForm.title.trim()) return;
          createTask.mutate({ ...taskForm, contactId: contact.id, status: 'TODO' } as Parameters<typeof createTask.mutate>[0], {
            onSuccess: () => { invalidate(); setShowTask(false); setTaskForm({ title: '', description: '', priority: 'MEDIUM', dueDate: '' }); },
          });
        }}
      >
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Title *</Label>
            <Input className="mt-1" placeholder="e.g. Send proposal" value={taskForm.title} onChange={(e) => setTaskForm((p) => ({ ...p, title: e.target.value }))} />
          </div>
          <div>
            <Label className="text-sm">Description</Label>
            <Textarea className="mt-1" placeholder="Details..." rows={2} value={taskForm.description} onChange={(e) => setTaskForm((p) => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Priority</Label>
              <Select value={taskForm.priority} onValueChange={(v) => setTaskForm((p) => ({ ...p, priority: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{priorityOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Due Date</Label>
              <Input className="mt-1" type="date" value={taskForm.dueDate} onChange={(e) => setTaskForm((p) => ({ ...p, dueDate: e.target.value }))} />
            </div>
          </div>
        </div>
      </FormDialog>
    </PageContainer>
  );
}
